import { useCallback, useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { gbpConnectHref, locationApi } from '@/lib/api-paths';
import { useCanManageGbp } from '@/hooks/use-can-manage-gbp';
import { useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { UpgradeGate } from '@pageboss/ui';
import { TERMS } from '@pageboss/shared';
import { suggestSearchTerms } from '@pageboss/shared/search-term-suggestions';
import { Skeleton } from '@/components/skeleton';
import { Input } from '@/components/ui/input';
import { NotConnectedPanel } from '@/components/gbp/not-connected-panel';
import { LocationPicker } from '@/components/gbp/location-picker';
import {
  useGbpData,
  fetchGbpForSelect,
  invalidateGbp,
  type GbpDataState,
} from '@/hooks/use-gbp-data';
import { api, type ApiError } from '@/lib/api';
import { gbpConnectError } from '@/lib/gbp-connect-errors';
import { HandledSaveError } from '@/lib/handled-save-error';
import type { GeoScheduleResponse, GeoScheduleSaveResponse } from '@/lib/types';

// =====================================================================
// VisibilityStep (WP3 Task 9) — the wizard's Visibility step BODY (spec
// §8 step 2). WizardStep chrome (title, continue/skip, "step N of 4") is
// supplied by the shell (Task 11); this component only renders what goes
// inside it: (1) the Google profile connect block, (2) the suggested
// search-terms block.
//
// One-primary-CTA-per-step (2026-08-17): `registerSave` used to be wired
// as an unconditional no-op resolve here — the search-terms block had its
// own inner "Save and start tracking" button, a second competing control
// alongside the wizard's bottom "save and continue". That inner button is
// gone; `registerSave` is now forwarded to `SearchTermsBlock`, which
// registers the REAL schedule-PUT as the step's save (see its own header
// comment for the gating + throw-on-failure contract). The Google-profile
// connect block still has no save of its own — connecting Google is an
// OAuth redirect (LocationPicker's POST), not a form save — so nothing
// changes there.
// =====================================================================

interface BusinessInfoGetResponse {
  businessInfo: { primaryCategory: string | null; addressCity: string | null };
}

type SaveOutcome =
  | { kind: 'scan_running' }
  | { kind: 'rate_limited' }
  | { kind: 'saved_no_scan' };

export function VisibilityStep({
  siteId,
  registerSave,
}: {
  siteId: string;
  registerSave?: (save: () => Promise<void>) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // The Business half of the canonical route this step is rendered under.
  // The Location half arrives as a prop from the wizard; neither is ever
  // inferred from the other.
  const businessId = useBusinessId() ?? '';
  // Connecting is owner-only (gbp:manage) — see NotConnectedPanel.
  const canManageGbp = useCanManageGbp();

  // Same capture-once-on-mount pattern as the pre-Task-8 site-gbp.tsx /
  // Task 8's google-profile.tsx — see hooks/use-gbp-data.ts's
  // fetchGbpForSelect doc comment for why this must fire exactly once and
  // bypass the hook's own auto-fetch. `?select=` is the callback's
  // multi-candidate return (auth/gbp.ts:445); `?error=` is every other
  // callback failure, including the honest "we couldn't find a business on
  // that Google account" case (A6 — gbp_no_locations, mapped by
  // gbpConnectError below).
  const [selectPending] = useState(() => searchParams.has('select'));
  const [connectErrorCode] = useState(() => searchParams.get('error'));
  const [connectError] = useState(() => gbpConnectError(connectErrorCode));

  useEffect(() => {
    if (!selectPending && !connectError) return;
    const next = new URLSearchParams(searchParams);
    next.delete('select');
    next.delete('error');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectPending && businessId && siteId) fetchGbpForSelect(businessId, siteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gbp = useGbpData(businessId, siteId, { skipAutoFetch: selectPending });

  const handlePickerChanged = useCallback(() => {
    invalidateGbp(businessId, siteId);
  }, [businessId, siteId]);

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold tracking-tight">Your {TERMS.googleProfile}</h2>
        {connectError ? (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning-fg"
          >
            <span className="max-w-prose">{connectError.message}</span>
            <div className="flex flex-wrap gap-4">
              {connectError.canSwitchAccount && canManageGbp ? (
                <a
                  href={gbpConnectHref(businessId, siteId, { from: 'setup', switchAccount: true })}
                  className="self-start text-sm font-semibold underline underline-offset-2"
                >
                  Try another Google account
                </a>
              ) : null}
              {/* A6: the honest cause of gbp_no_locations is ambiguous — wrong
                  Google account (covered above) OR no listing exists yet at
                  all. Google's own create/claim flow is the real fix for the
                  second case; we never build our own listing-creation UI
                  (no autopilot, no new API surface). */}
              {connectErrorCode === 'gbp_no_locations' ? (
                <a
                  href="https://business.google.com/create"
                  target="_blank"
                  rel="noreferrer"
                  className="self-start text-sm font-semibold underline underline-offset-2"
                >
                  Claim or create your Google listing
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
        <GoogleProfileBlock siteId={siteId} gbp={gbp} onPickerChanged={handlePickerChanged} />
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold tracking-tight">Search terms to track</h2>
        <SearchTermsBlock siteId={siteId} {...(registerSave ? { registerSave } : {})} />
      </section>
    </div>
  );
}

function GoogleProfileBlock({
  siteId,
  gbp,
  onPickerChanged,
}: {
  siteId: string;
  gbp: GbpDataState;
  onPickerChanged: () => void;
}) {
  if (gbp.kind === 'loading') {
    return <Skeleton className="h-32 w-full rounded-xl" />;
  }
  if (gbp.kind === 'error') {
    return (
      <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
        {gbp.message}
      </div>
    );
  }
  if (gbp.data.state === 'locked') {
    return (
      <UpgradeGate
        missing="Google profile tools are part of a bigger plan."
        planKey="pro"
      />
    );
  }
  if (gbp.data.state === 'not_connected') {
    return <NotConnectedPanel siteId={siteId} returnTo="setup" />;
  }
  if (gbp.data.state === 'pending_selection') {
    return <LocationPicker candidates={gbp.data.candidates} onChanged={onPickerChanged} />;
  }
  // connected
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-card p-6">
      <Check className="h-5 w-5 shrink-0 text-brand-600" aria-hidden />
      <div>
        <p className="text-sm font-semibold text-foreground">Connected</p>
        <p className="text-sm text-text-secondary">{gbp.data.connection.locationName}</p>
      </div>
    </div>
  );
}

// One-primary-CTA-per-step (2026-08-17): this block used to have its own
// "Save and start tracking" button, wired to nothing but itself — the
// wizard's bottom "save and continue" was a separate, unrelated no-op. That
// inner button is gone; `registerSave` (forwarded from the shell via
// VisibilityStep) now receives `performSave` below, so the bottom CTA
// performs the real schedule PUT before advancing. All of this block's own
// async result state (the specific error/upgrade-gate/outcome copy) is
// UNCHANGED — it still renders inline in this same card, just triggered by
// the bottom CTA instead of a local click handler.
//
// Gating: `performSave` re-registers fresh on every render (no ref
// plumbing, mirrors business-info-panel.tsx's same-day fix) and is a safe
// no-op whenever there is nothing real to save — still loading, the load
// failed, an existing registry is already showing (read-only, no form), a
// prior save already parked this card on the upgrade gate, or every term
// input is empty (search terms are optional, same as the old disabled-
// button guard: `canSave`). On an actual failure it sets its usual specific
// inline message/gate AND re-throws, so the wizard blocks Continue's
// advance and the owner sees why instead of silently moving on — same
// contract as business-info-panel.tsx's `save`.
function SearchTermsBlock({
  siteId,
  registerSave,
}: {
  siteId: string;
  registerSave?: (save: () => Promise<void>) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Already-registered registry (brief: read-only "already tracking N search
  // terms", suggestion skipped). Split into count + list rather than storing
  // the whole GeoScheduleResponse['schedule'] row so no JSX line here ever
  // reads the wire field name directly (glossary sweep guard, WP1 Task 14 —
  // the customer NOUN is TERMS.searchTerms, the wire field stays `keywords`
  // per locked decision #21, and the two must not mix on a rendered line).
  const [hasExistingRegistry, setHasExistingRegistry] = useState(false);
  const [existingCount, setExistingCount] = useState(0);
  const [existingTerms, setExistingTerms] = useState<string[]>([]);
  const [hasCategory, setHasCategory] = useState(true);
  const [terms, setTerms] = useState<string[]>(['', '', '']);

  const [saving, setSaving] = useState(false);
  const [saveOutcome, setSaveOutcome] = useState<SaveOutcome | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [upgradeGate, setUpgradeGate] = useState<{ missing: string } | null>(null);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;

    Promise.all([
      api.get<BusinessInfoGetResponse>(locationApi(businessId, siteId, 'business-info')),
      api.get<GeoScheduleResponse>(locationApi(businessId, siteId, 'geo/schedule')),
    ])
      .then(([businessInfoRes, scheduleRes]) => {
        if (cancelled) return;
        if (scheduleRes.schedule) {
          const wireTerms = scheduleRes.schedule?.keywords ?? [];
          setHasExistingRegistry(true);
          setExistingCount(wireTerms.length);
          setExistingTerms(wireTerms);
        } else {
          const suggested = suggestSearchTerms({
            category: businessInfoRes.businessInfo.primaryCategory,
            city: businessInfoRes.businessInfo.addressCity,
          });
          setHasCategory(suggested.length > 0);
          // Fix round 1: suggestSearchTerms returns fewer than 3 entries
          // when a category is set but the city isn't (2 patterns, no
          // fabricated place name) — always render THREE input boxes
          // regardless, padding with empty ones the owner can fill in
          // themselves. Padding never adds a fabricated term; handleSave's
          // trim+filter already drops the empties before the PUT.
          setTerms([...suggested, '', '', ''].slice(0, 3));
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  const canSave = terms.some((t) => t.trim().length > 0);

  async function performSave(): Promise<void> {
    // Gating (see the block-level header comment above): a no-op resolve
    // in every state where there's genuinely nothing to save — still
    // loading, the load failed, already showing the read-only "already
    // tracking" registry, already parked on the upgrade gate from a prior
    // attempt, or every term box is empty (search terms are optional, same
    // as the old disabled-button guard).
    if (loading || loadError || hasExistingRegistry || upgradeGate || !canSave) return;
    setSaving(true);
    setSaveError(null);
    setSaveOutcome(null);
    setUpgradeGate(null);
    try {
      const keywords = terms.map((t) => t.trim()).filter((t) => t.length > 0);
      const res = await api.put<GeoScheduleSaveResponse>(locationApi(businessId, siteId, 'geo/schedule'), {
        enabled: true,
        keywords,
      });
      if (res.scanId) {
        setSaveOutcome({ kind: 'scan_running' });
      } else if (res.enqueueError === 'rate_limited') {
        setSaveOutcome({ kind: 'rate_limited' });
      } else {
        setSaveOutcome({ kind: 'saved_no_scan' });
      }
    } catch (err) {
      const e = err as ApiError;
      const code =
        e.body && typeof e.body === 'object' && 'error' in e.body
          ? String((e.body as { error: unknown }).error)
          : null;
      if (e.status === 403 && code === 'tier_not_eligible') {
        setUpgradeGate({ missing: 'Tracking search terms is part of a bigger plan.' });
      } else if (e.status === 409 && code === 'identity_missing') {
        setSaveError('connect your Google profile above first — then we can find your business on the map.');
      } else if (e.status === 409 && code === 'location_missing') {
        setSaveError('add your business address in the foundation step first.');
      } else if (e.status === 400 && code === 'no_search_terms') {
        setSaveError('Add at least one search term.');
      } else if (e.status === 400 && code === 'invalid_search_term') {
        setSaveError('One of your search terms is too long or empty. Please check them and try again.');
      } else if (e.status === 403 && code === 'capacity_exceeded') {
        const limit =
          e.body && typeof e.body === 'object' && 'limit' in e.body
            ? Number((e.body as { limit: unknown }).limit)
            : null;
        setSaveError(
          limit != null
            ? `Your plan includes up to ${limit} search terms.`
            : "Your plan's search-term limit was reached.",
        );
      } else {
        setSaveError("Couldn't save your search terms right now. Please try again.");
      }
      // Re-throw: in the wizard this is what blocks Continue's advance on a
      // failed/gated save so the owner sees the specific message above
      // (upgrade gate, missing identity, capacity, etc.) instead of the
      // page silently moving on to the next step — same contract as
      // business-info-panel.tsx's `save`. Wrapped in HandledSaveError
      // (dup-banner fix, 2026-08-21): every catch branch above already
      // rendered its own copy, so the shell must block the advance WITHOUT
      // stacking its generic banner on top.
      throw new HandledSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  // Re-registers every render with a fresh closure (mirrors business-info-
  // panel.tsx) so `performSave` always reads the LATEST terms/gating state.
  useEffect(() => {
    registerSave?.(performSave);
  });

  if (loading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  if (loadError) {
    return (
      <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
        Couldn't load your search terms right now.
      </div>
    );
  }

  if (upgradeGate) {
    return <UpgradeGate missing={upgradeGate.missing} planKey="pro" />;
  }

  if (hasExistingRegistry) {
    return (
      <div className="rounded-xl border border-border-subtle bg-card p-6">
        <p className="text-sm font-semibold text-foreground">
          You're already tracking {existingCount} search term
          {existingCount === 1 ? '' : 's'}.
        </p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {existingTerms.map((term) => (
            <li
              key={term}
              className="rounded-full bg-surface-sunken px-3 py-1 text-xs font-medium text-text-secondary"
            >
              {term}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border-subtle bg-card p-6">
      <p className="text-sm text-text-secondary">
        We track how you rank on Google's map for these searches, every week.
      </p>
      {!hasCategory ? (
        <p className="mt-2 text-sm text-text-tertiary">
          add your business category in the foundation step and we'll suggest terms
        </p>
      ) : null}

      <div className="mt-4 flex flex-col gap-3">
        {terms.map((term, i) => (
          <div key={i} className="space-y-1.5">
            <label
              htmlFor={`visibility-search-term-${i}`}
              className="block text-sm font-medium text-foreground"
            >
              Search term {i + 1}
            </label>
            <Input
              id={`visibility-search-term-${i}`}
              value={term}
              onChange={(e) => {
                const next = [...terms];
                next[i] = e.target.value;
                setTerms(next);
              }}
            />
          </div>
        ))}
      </div>

      {saving ? (
        <p role="status" className="mt-3 text-sm text-text-secondary">
          Saving…
        </p>
      ) : null}

      {saveError ? (
        <p role="alert" className="mt-3 text-sm text-danger-fg">
          {saveError}
        </p>
      ) : null}

      {saveOutcome?.kind === 'scan_running' ? (
        <p role="status" className="mt-3 text-sm text-brand-700">
          {/* FIRST-CHECK-NOW: apps/dashboard/src/routes/api/geo.ts:1106-1167 —
              this is the actual day-one setup scan the schedule save
              triggers, not a fabricated result. Never show numbers here. */}
          Your first scan is running now — results land on your rank map today.
        </p>
      ) : null}
      {saveOutcome?.kind === 'rate_limited' ? (
        <p role="status" className="mt-3 text-sm text-text-secondary">
          Today's check already ran — your next one runs Monday.
        </p>
      ) : null}
      {saveOutcome?.kind === 'saved_no_scan' ? (
        <p role="status" className="mt-3 text-sm text-text-secondary">
          Saved — your weekly check starts Monday.
        </p>
      ) : null}
    </div>
  );
}
